import { PipelineBuildDetailsMetadataCommit } from "@microtica/ms-ap-sdk";
import { MicroserviceConfigurationItem, StageDeploymentResponseLogsErrorMessages } from "@microtica/ms-elasticsearch-sdk";
import { GetOutputsForResourceResponseOutputs, GetStageDetailsResponseResources, GetStageResourcesResponseResources, GetStageResponseCloudProviderEnum, GetStageResponseInfrastructureAsCodeToolEnum } from "@microtica/ms-engine-sdk";
import { GetDeployedMicroservicesInStageResponseDeployedMicroservices, GetKubernetesStatusResponseTypeEnum, ListKubernetesResponseKubernetesesTypeEnum } from "@microtica/ms-kube-sdk";
import { InlineResponse200PaymentPlans } from "@microtica/ms-project-sdk";
import { ReactNode } from "react";

export type WindowConfig = Window & {
    woopra: {
        identify: (data: object) => void;
        track: (event: string, data?: object) => void;
        push: (callback?: Function) => void;
    };
    gist: {
        identify: (obj: { email: string }) => void;
        track: (event: string, data?: object) => void;
        chat: (event: string, action: string) => void;
    };
    _cio: {
        identify: (obj: { id: string; email: string }) => void;
        track: (event: string, data?: object) => void;
    }
};


export type AwsRegionType = "UsEast2" | "UsEast1" | "UsWest1" | "UsWest2" | "ApEast1" |
    "ApSouth1" | "ApNortheast3" | "ApNortheast2" | "ApSoutheast1" |
    "ApSoutheast2" | "ApNortheast1" | "CaCentral1" | "CnNorth1" |
    "CnNorthwest1" | "EuCentral1" | "EuWest1" | "EuWest2" |
    "EuWest3" | "EuNorth1" | "MeSouth1" | "SaEast1" | "UsGovEast1" | "UsGovWest1";

export interface Dictionary<T> {
    [key: string]: T;
}

export type GitProvider = "github" | "bitbucket" | "gitlab";

export interface EnvVars extends NodeJS.ProcessEnv {
    REACT_APP_BACKEND_ENDPOINT: string;
    REACT_APP_COGNITO_HOSTED_UI_URL: string;
    REACT_APP_COGNITO_OAUTH_CLIENT_ID: string;
    REACT_APP_GITHUB_CLIENT_ID: string;
    REACT_APP_GITLAB_CLIENT_ID: string;
    REACT_APP_GITLAB_REDIRECT_URL: string;
    REACT_APP_BITBUCKET_CLIENT_ID: string;
    REACT_APP_STRIPE_PUBLISHABLE_KEY: string;
    REACT_APP_GIST_TOKEN: string;
    REACT_APP_MIXPANEL_TOKEN: string;
    REACT_APP_HELPHERO_APP_ID: string;
    REACT_APP_OLD_PORTAL_URL: string;
}
export interface IProjectDetails {
    id: string;
    name: string;
    description: string;
}

export interface ComponentDeployment {
    id: string;
    name: string;
    version: string;
    commitType: string;
    commitMessage: string;
    repositoryUrl: string;
    branch: string;
    createdAt: string;
    createdBy: string;
    pipeline?: {
        id: string;
        buildId: string;
        public: boolean;
    };
    initiator?: {
        name: string;
        externalId: string;
        avatar: string;
    };
    status: {
        name: string;
        errors?: {
            logicalResourceId: string;
            resourceType: string;
            statusReason: string;
        };
        error?: string;
    };
    timestamp: string;
}

export interface IAppMonitoring {
    memoryUsage: string;
    initialMemory: number;
    memoryLimit: number;
    cpuUsage: string;
    initialCpu: number;
    cpuLimit: number;
    runningPods: number;
    failedPods: number;
    minReplicas: number;
    maxReplicas: number;
    pods: {
        name: string;
        phase: PodPhase;
        containers: {
            name: string;
            image: string;
            restartCount: number;
            ready: boolean;
        }[]
    }[];
}

export interface IAppDetails {
    name: string;
    description: string;
    domain: string;
    isDomainWorking: boolean;
    lastDeploymentIncludesIngressSetup: boolean;
    branch: string;
    createdAt: string;
    version: string;
    image: string;
    status: string;
    commitMessage: string;
    commitDate?: string;
    namespace: string;
    isPublic: boolean;
    clusterId: string;
    schema: ComponentSchema;
    configuration?: ComponentConfig[],
    deployments: ComponentDeployment[];
    repositoryUrl: string;
    pipelineId: string;
    monitoring: IAppMonitoring;
    clusterType: GetKubernetesStatusResponseTypeEnum;
    associatedNamespace?: string;
}

export interface IEnvDetails {
    id: string;
    name: string;
    description: string;
    resources: GetStageResourcesResponseResources[];
    isUndeployDisabled: boolean;
    deployedMicroservices: GetDeployedMicroservicesInStageResponseDeployedMicroservices[];
    deploymentsUsed: number;
    totalMicroservices: number;
    totalHealthyMicroservices: number;
    totalFailedMicroservices: number;
    totalDeployedResources: number;
    refreshRoot: () => Promise<void>;
    status?: string;
    lastDeployed?: string;
    awsAccountId?: string;
    region?: string;
    infrastructureAsCodeTool?: GetStageResponseInfrastructureAsCodeToolEnum;
    cloudProvider?: GetStageResponseCloudProviderEnum;
    lastDeploymentId?: string;
}

export interface IResourceDetails {
    name: string;
    status: string;
    version: string;
    component: ComponentDetails;
    isPublic: boolean;
    configurations: ComponentConfig[];
    currentDeploymentId?: string;
    previousDeploymentId?: string;
    pipelineId: string;
    isDeployed: boolean;
    deployments: ComponentDeployment[];
    awsAccountId: string;
    awsRegion: string;
    domain: {
        displayAssignDomainBtn: boolean;
        eligibleForDomainConfiguration: boolean;
        accessUrl?: string;
        CNAME?: string;
    },
    environment: {
        status: string;
        resources: GetStageDetailsResponseResources[];
    }
    refreshRoot: () => Promise<void>;
    outputs?: GetOutputsForResourceResponseOutputs[];
    outputsErrorMessage?: string;
}

export interface ComponentDetails {
    id: string;
    name: string;
    description: string;
    schema: ComponentSchema;
    pipelineId: string;
    isPublic: boolean;
}

export interface TemplateResourceProperty {
    key: string;
    value?: string;
    reference?: string;
}

export interface Template {
    name: string;
    description: string;
    coverImage: string;
    logo: string;
    repo: string;
    docs: string;
    inputs?: {
        type: "object";
        properties: ComponentSchemaInputProperties;
        required?: string[];
    };
    infrastructure?: {
        resources: Dictionary<{
            type?: string;
            source?: "git";
            properties: TemplateResourceProperty[]
        }>
    }
    git?: boolean;
    service?: Dictionary<{
        source?: "git";
        image?: string;
        registry?: string;
        containerPort?: number;
        autoScaling?: {
            minReplicas: number;
            maxReplicas: number;
        }
        configuration?: ComponentConfig[];
        deployment: {
            options: [
                "shared",
                "personal",
                "managed"
            ]
        };
        pipeline?: object;
    }>
}

export interface ExecutingTemplate {
    buildId: string;
    templateExecutionId: string;
    env: EnvironmentDetails;
    title: string;
    repo: string;
    started?: number;
    finished?: number;
    status?: string;
    type?: TemplateType;
    appName: string;
    gitAccount: { accountId: string; fullName: string; autoDeploy: boolean; branch: string; repositoryUrl: string; }
    framework: string;
    clusterDetails: {
        creatingCluster: boolean,
        clusterAppName?: string;
    }
}

export interface DeploymentStage {
    name: string;
    current: boolean;
    status: string;
    result?: ReactNode;
    error?: string;
}

export interface DeploymentState {
    stages: DeploymentStage[];
    status: string;
    action?: ReactNode;
    current?: boolean;
    disabled?: boolean;
    loading?: boolean;
    error?: string;
}

export interface SharedDestination {
    type: "shared" | "managed";
}

export interface Destination {
    type: ListKubernetesResponseKubernetesesTypeEnum;
    accountId: string;
    region: string;
    clusterId?: string;
    clusterNamespace?: string;
}

export interface EnvironmentDetails {
    id: string;
    name: string;
    cloudProvider: string;
    accountId?: string;
    region?: string;
    clusterId?: string;
    clusterName?: string;
    clusterNamespace?: string;
    clusterType?: string;
}

export type TemplateType = "cluster" | "environment" | "both";

export interface Environment {
    id: string;
    name: string;
    cloudProvider: string;
    accountId?: string;
    region?: string;
}

export interface NavigationItem {
    id: string;
    name: string;
}

export interface NavigationState {
    project: NavigationItem;
    environment?: NavigationItem;
    app?: NavigationItem;
}

export type NavigationActionTypes = "project:set" | "environment:set" | "environment:unset" | "app:set" | "app:unset";

export interface PricingPlan {
    id: string;
    name: string;
    description: string;
    features: string[];
    priceId?: string;
    price?: string;
    limitations?: string;
    active?: boolean;
}
export interface PaymentPlansResponse extends InlineResponse200PaymentPlans {
    displayOrder: number;
}

export type ComponentSchemaInputProp = {
    label?: string;
    type: "string" | "number" | "array";
    dataType?: "string" | "number" | "array";
    enum?: string[];
    default?: string | number;
    description?: string;
    placeholder?: string;
    minimum?: number;
    maximum?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    patternErrorMessage?: string;
    sensitive?: boolean;
    reference?: boolean;
    category?: "advanced" | "scaling" | "autoscaling" | "allocation" | "alarm" | "storage" | "variables" | "domain";
    subcategory?: "replicas" | "allocation";
}

export type ComponentSchemaInputProperties = Dictionary<ComponentSchemaInputProp>;
export interface ComponentSchema {
    properties: {
        inputs: {
            type: "object";
            properties: ComponentSchemaInputProperties;
            required?: string[];
            additionalProperties?: boolean;
        };
        outputs?: {
            type: "object";
            properties:
            Dictionary<{ type: "string"; description?: string; }>;
            required?: string[];
            additionalProperties?: boolean;
        }
    },
    required?: string[];
}

export interface ComponentConfig {
    key: string;
    value: string;
    sensitive?: boolean;
    reference?: boolean;
    required?: boolean;
}

export interface UserProfile {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

export interface PipelineSpec {
    stages?: string[];
    steps: Dictionary<{
        type: string;
        pipeline?: string;
        service: string;
    }>;
}

export interface ComponentVersion {
    name: string;
    date?: number;
    commit: PipelineBuildDetailsMetadataCommit
}

export type AllClusters = Dictionary<{
    env: Omit<Environment, "cloudProvider">;
    clusters: { id: string; name: string; type: ListKubernetesResponseKubernetesesTypeEnum }[]
}>;

export type FormState = "editing" | "disabled" | "saved";

export type DeploymentType = "infrastructure" | "app";
export interface EnvDeployment {
    id: string;
    type: DeploymentType;
    active: boolean;
    createdAt: string;
    timestamp: string;
    createdBy: string;
    error?: string;
    status: {
        name: string;
        errors: StageDeploymentResponseLogsErrorMessages[];
    },
    resources: EnvDeploymentResource[];
}

export interface EnvDeploymentResource {
    id: string;
    timestamp: string;
    name: string;
    deploymentId: string;
    kubernetesId?: string;
    kubernetesName?: string;
    kubernetesNamespace?: string;
    version: string;
    branch: string;
    commitType: string;
    commitMessage: string;
    repositoryUrl: string;
    pipeline: {
        id: string;
        buildId: string;
        public: boolean;
    },
    createdAt: string;
    createdBy: string;
    avatarUrl: string;
    status: {
        name: string;
        errors?: StageDeploymentResponseLogsErrorMessages[];
    },
    configurations: MicroserviceConfigurationItem[];
    prev?: {
        version: string;
        branch: string;
        repositoryUrl?: string;
        equal: boolean;
        changes: MicroserviceConfigurationItem[][];
        commitType: string;
    }
}

export enum StatusColor {
    Active = "#52c41a",
    Processing = "#097bb5",
    Failed = "#ff4d4f",
    Default = "graytext"
}

export enum WidgetColor {
    Blue = "#097bb5",
    FadedBlue = "#64afd5"
}

export type PodPhase = "Pending" | "Running" | "Succeeded" | "Failed" | "Unknown";

export interface ResourceConfigurationAndStatus {
    [resourceName: string]: {
        status: string;
        configurations: ComponentConfig[]
    }
}

export interface ISavingScheduleAggregatedUtilization {
    totalUtilizedHours: number;
    estimatedSavingPercentage: number;
    dailyUtilizedHours: number[];
    startHourInTimeZone: number;
    stopHourInTimeZone: number;
    utilizedDays: string[];
}